import { Box, Chip, Stack } from '@mui/material';
import { chunk } from 'lodash';
import * as React from 'react';
import { Image } from 'remix-image';
import ShopCarousel from '../Shop/ShopCarousel';

export default function AutomockupCarousel({
  message,
  isSubmitting,
  handleChange,
  logos,
  currentMockupLogo,
}: {
  currentMockupLogo: string | null;
  handleChange: (logoUrl: string) => void;
  isSubmitting: boolean;
  message: React.ReactNode;
  logos: string[];
}) {
  // set current logo to be first
  const orderedLogos = [
    ...logos.filter((logo) => logo === currentMockupLogo),
    ...logos.filter((logo) => logo !== currentMockupLogo),
  ];

  const [selectedLogo, setSelectedLogo] = React.useState(orderedLogos[0]);

  return (
    <Stack>
      <Stack justifyContent="center" alignItems="center" mt={4}>
        <AutoMockupSlides
          width={140}
          height={140}
          url={selectedLogo}
          isCurrentMockupLogo={selectedLogo === currentMockupLogo}
        />
        <Stack width="100%" mt={2}>
          {orderedLogos.length > 1 && (
            <ShopCarousel
              disableButtons={isSubmitting}
              autoPlay={false}
              containerSx={{ height: 50, width: '100%' }}
              buttonSx={{
                bgcolor: 'transparent',
                border: 'none',
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              indicatorContainerSx={{ left: 0, bottom: '-30px', width: '100%' }}
              indicatorIconButtonSx={{ height: '8px', width: '8px', marginLeft: '8px', marginRight: '8px' }}
              activeIndicatorIconButtonProps={{
                style: { color: '#009DC8', backgroundColor: '#009DC8', height: '8px', width: '8px' },
              }}
            >
              {chunk(orderedLogos, 5).map((logosChunk, i) => {
                return (
                  <Stack
                    key={i}
                    height={50}
                    flexDirection="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    px={2}
                    sx={{ '& > div:nth-of-type(n+2)': { ml: '20px' } }}
                  >
                    {logosChunk.map((logo) => (
                      <Box
                        key={logo}
                        sx={{ height: 50, cursor: 'pointer' }}
                        onClick={() => {
                          setSelectedLogo(logo);
                          handleChange(logo);
                        }}
                      >
                        <AutoMockupSlides isSelected={logo === selectedLogo} width={50} height={50} url={logo} />
                      </Box>
                    ))}
                  </Stack>
                );
              })}
            </ShopCarousel>
          )}
        </Stack>
      </Stack>
      {message}
    </Stack>
  );
}

export function AutoMockupSlides({
  url,
  width,
  height,
  isSelected,
  isCurrentMockupLogo = false,
}: {
  url: string;
  width: number;
  height: number;
  isSelected?: boolean;
  isCurrentMockupLogo?: boolean;
}) {
  return (
    <Stack justifyContent="center" alignItems="center" height={height} position="relative">
      {isCurrentMockupLogo && (
        <Chip
          label="Selected"
          color="primary"
          variant="filled"
          sx={{ width: 'fit-content', position: 'absolute', inset: '-15px 70%' }}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: width > 100 ? 2 : 0.5,
          width: width,
          height: height,
          borderRadius: 2,
          border: isSelected ? '2px solid #009DC8' : '1px solid #bfbfbf',
          backgroundImage:
            'repeating-linear-gradient(45deg, #EBEBEB 25%, transparent 25%, transparent 75%, #EBEBEB 75%, #EBEBEB), repeating-linear-gradient(45deg, #EBEBEB 25%, #F5F5F5 25%, #F5F5F5 75%, #EBEBEB 75%, #EBEBEB)',
          backgroundPosition: '0 0, 6px 6px',
          backgroundSize: '12px 12px',
        }}
      >
        <Image
          width={width - 20}
          height={width - 20}
          options={{ fit: 'inside' }}
          responsive={[{ size: { width: width - 20, height: width - 20 } }]}
          dprVariants={[1, 3]}
          loading="lazy"
          src={url}
        />
      </Box>
    </Stack>
  );
}
